import type { AnalyticEvents } from '@/types/analytics'
import { withTimeout } from '@/utils/withTimeout'
import type { IdentifyParams, PageParams } from '@segment/analytics-next/dist/types/core/arguments-resolver'
import type { User } from '@segment/analytics-next'
import snakecaseKeys from 'snakecase-keys'
import { type AnalyticsOptions, getAnalyticsClients } from '@/plugins/analyticsClients'

const SEGMENT_ANONYMOUS_USER_ID_KEY = 'ajs_anonymous_id'

const safely = (callback: () => unknown): void => {
  try {
    callback()
  } catch {
    // Analytics failures (for example ad blockers) should never break account flows.
  }
}

const transformEvent = (eventData?: Record<string, unknown> | null): Record<string, unknown> => {
  // CA has historically snake_cased Segment payloads, so the shared facade keeps that warehouse contract for both sinks.
  return snakecaseKeys(eventData ?? {})
}

const getOrCreateAnonymousId = (): string => {
  const existingAnonymousId = localStorage.getItem(SEGMENT_ANONYMOUS_USER_ID_KEY)

  if (existingAnonymousId) {
    return existingAnonymousId
  }

  const uuid = crypto.randomUUID()
  localStorage.setItem(SEGMENT_ANONYMOUS_USER_ID_KEY, uuid)

  return uuid
}

export const useAnalytics = () => {
  const track = <K extends keyof AnalyticEvents>(
    eventName: K,
    eventData?: AnalyticEvents[K],
    options?: AnalyticsOptions,
  ): void => {
    const { customerio, segment } = getAnalyticsClients()
    const transformedData = transformEvent(eventData as Record<string, unknown> | null | undefined)

    safely(() => customerio?.track(eventName, transformedData, options))
    safely(() => segment?.track(eventName, transformedData, options))
  }

  const identify = (...args: IdentifyParams): void => {
    const { customerio, segment } = getAnalyticsClients()

    safely(() => customerio?.identify(...args))
    safely(() => segment?.identify(...args))
  }

  const page = (...args: PageParams): void => {
    const { customerio, segment } = getAnalyticsClients()

    safely(() => customerio?.page(...args))
    safely(() => segment?.page(...args))
  }

  const reset = (): void => {
    const { customerio, segment } = getAnalyticsClients()

    safely(() => customerio?.reset())
    safely(() => segment?.reset())
  }

  const trackLink = (
    element: HTMLAnchorElement,
    eventName: keyof AnalyticEvents,
    eventData?: Record<string, unknown>,
    options?: AnalyticsOptions,
  ): void => {
    const { customerio, segment } = getAnalyticsClients()
    const transformedData = transformEvent(eventData)

    safely(() => customerio?.trackLink?.(element, eventName, transformedData, options))
    safely(() => segment?.trackLink?.(element, eventName, transformedData, options))
  }

  const getUser = async (): Promise<User | undefined> => {
    try {
      const { segment } = getAnalyticsClients()

      const user = segment?.user

      if (!user) {
        return undefined
      }

      return await withTimeout<User>(user, 2_000)
    } catch {
      return undefined
    }
  }

  const anonymousId = async (): Promise<string> => {
    const user = await getUser()
    const id = user?.anonymousId()

    if (id) {
      return id
    }

    return getOrCreateAnonymousId()
  }

  return {
    anonymousId,
    getAnonymousId: anonymousId,
    identify,
    page,
    reset,
    track,
    trackLink,
  }
}
